import { CommonModule } from '@angular/common';
import { Component, input, signal, computed, viewChild, ChangeDetectionStrategy, ElementRef, AfterViewInit, OnDestroy, NgZone, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelect } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SelectAllDirective } from '../../directives/select-all.directive';

export interface MultiSelectOption {
  id: number | string;
  name: string;
}

const DEFAULT_MAX_CHIPS = 10;
const CHIP_GAP = 7.2; // 0.45rem in pixels
const MORE_CHIP_WIDTH = 70; // approximate width of "+N more" chip
const CLEAR_BTN_WIDTH = 30; // clear button + margin

@Component({
  selector: 'multi-select-chip-field',
  templateUrl: './multi-select-chip-field.component.html',
  styleUrls: ['./multi-select-chip-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ CommonModule, FormsModule, MatFormFieldModule, MatSelectModule, MatTooltipModule, SelectAllDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MultiSelectChipFieldComponent,
      multi: true
    }
  ]
})
export class MultiSelectChipFieldComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private readonly ngZone = inject(NgZone);

  label = input.required<string>();
  placeholder = input.required<string>();
  selectId = input.required<string>();
  options = input.required<MultiSelectOption[]>();

  readonly matSelect = viewChild<MatSelect>('matSelect');
  readonly chipRowRef = viewChild<ElementRef<HTMLElement>>('chipRow');
  readonly measureContainer = viewChild<ElementRef<HTMLElement>>('measureContainer');

  value = signal<(number | string)[]>([]);
  disabled = signal(false);
  visibleChipCount = signal(DEFAULT_MAX_CHIPS);

  private onChange: (value: (number | string)[]) => void = () => {};
  private onTouched: () => void = () => {};
  private resizeObserver: ResizeObserver | null = null;

  readonly optionNameById = computed(() => {
    const opts = this.options();
    return new Map(opts.map(o => [o.id, o.name] as const));
  });

  readonly allOptionIds = computed(() => this.options().map(o => o.id));

  readonly selectedNames = computed(() => {
    const ids = this.value();
    const map = this.optionNameById();
    return ids.map(id => map.get(id)).filter((v): v is string => typeof v === 'string');
  });

  readonly visibleNames = computed(() => {
    const names = this.selectedNames();
    return names.slice(0, this.visibleChipCount());
  });

  readonly hiddenCount = computed(() => {
    const names = this.selectedNames();
    const visible = this.visibleChipCount();
    return Math.max(0, names.length - visible);
  });

  readonly moreTooltip = computed(() => {
    const names = this.selectedNames();
    const visible = this.visibleChipCount();
    return names.slice(visible).join(', ');
  });

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    setTimeout(() => this.recalculateVisibleChips(), 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private setupResizeObserver(): void {
    const chipRow = this.chipRowRef()?.nativeElement;
    if (!chipRow) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => this.recalculateVisibleChips());
    });
    this.resizeObserver.observe(chipRow);
  }

  private recalculateVisibleChips(): void {
    const container = this.measureContainer()?.nativeElement;
    const chipRow = this.chipRowRef()?.nativeElement;
    if (!container || !chipRow) return;

    const names = this.selectedNames();
    if (names.length === 0) {
      this.visibleChipCount.set(DEFAULT_MAX_CHIPS);
      return;
    }

    // Get available width (subtract clear button space)
    const availableWidth = chipRow.clientWidth - CLEAR_BTN_WIDTH;
    if (availableWidth <= 0) return;

    // Measure each chip's width
    const chipWidths: number[] = [];
    const measureChips = container.querySelectorAll('.chip--measure');
    measureChips.forEach((chip) => {
      chipWidths.push((chip as HTMLElement).offsetWidth);
    });

    // Calculate how many chips fit
    let totalWidth = 0;
    let count = 0;
    const hasMore = names.length > 1;

    for (let i = 0; i < chipWidths.length; i++) {
      const chipWidth = chipWidths[i];
      const wouldNeedMore = i < names.length - 1;
      const reserveForMore = wouldNeedMore ? MORE_CHIP_WIDTH + CHIP_GAP : 0;
      const nextWidth = totalWidth + chipWidth + (count > 0 ? CHIP_GAP : 0) + reserveForMore;

      if (nextWidth <= availableWidth) {
        totalWidth += chipWidth + (count > 0 ? CHIP_GAP : 0);
        count++;
      } else {
        break;
      }
    }

    this.visibleChipCount.set(Math.max(1, count));
  }

  writeValue(value: (number | string)[]): void {
    this.value.set(value || []);

    setTimeout(() => this.recalculateVisibleChips(), 0);
  }

  registerOnChange(fn: (value: (number | string)[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onValueChange(value: (number | string)[]): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();

    setTimeout(() => this.recalculateVisibleChips(), 0);
  }

  clearValue(): void {
    this.onValueChange([]);
  }

  openSelect(): void {
    const select = this.matSelect();
    if (select) {
      select.open();
    }
  }
}
